import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { register, clearError } from '../../store/slices/authSlice';
import AuthLayout from './AuthLayout';
import { showToast } from '../utils/Toast';

const AddUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    profile: {
      firstName: '',
      lastName: '',
      phone: '',
      role: 'admin',
      company: {
        name: '',
        position: '',
        department: ''
      }
    }
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const roles = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'admin', label: 'Admin' },
    { value: 'project_manager', label: 'Project Manager' }
  ];

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.profile.firstName) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.profile.lastName) {
      newErrors.lastName = 'Last name is required';
    }

    if (formData.profile.phone && !/^\+?[\d\s-]+$/.test(formData.profile.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 3) { // For company fields
        setFormData(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            company: {
              ...prev.profile.company,
              [parts[2]]: value
            }
          }
        }));
      } else if (parts.length === 2) { // For other profile fields
        setFormData(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            [parts[1]]: value
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Show validation errors in toast
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        errorMessages.forEach(error => {
          showToast(error, 'error');
        });
      }
      return;
    }

    try {
      const result = await dispatch(register(formData)).unwrap();
      showToast(result.message || 'User created successfully!', 'success');
    } catch (err) {
      // Show server error in toast
      showToast(err, 'error');
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-md">
        <h2 className="text-3xl font-bold mb-6">Add New User</h2>
        
        {success && (
          <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
            User created successfully!
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div>
            <input 
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email address"
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              } focus:outline-none focus:ring-2`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <input 
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.password ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              } focus:outline-none focus:ring-2`}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-500">{errors.password}</p>
            )}
          </div>

          {/* First Name */}
          <div>
            <input 
              type="text"
              name="profile.firstName"
              value={formData.profile.firstName}
              onChange={handleChange}
              placeholder="First Name"
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.firstName ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              } focus:outline-none focus:ring-2`}
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <input 
              type="text"
              name="profile.lastName"
              value={formData.profile.lastName}
              onChange={handleChange}
              placeholder="Last Name"
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.lastName ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              } focus:outline-none focus:ring-2`}
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <input 
              type="tel"
              name="profile.phone"
              value={formData.profile.phone}
              onChange={handleChange}
              placeholder="Phone number"
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.phone ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              } focus:outline-none focus:ring-2`}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <select
              name="profile.role"
              value={formData.profile.role}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {roles.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          {/* Company Details */}
          <div className="space-y-4">
            <input 
              type="text"
              name="profile.company.name"
              value={formData.profile.company.name}
              onChange={handleChange}
              placeholder="Company Name"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input 
              type="text"
              name="profile.company.position"
              value={formData.profile.company.position}
              onChange={handleChange}
              placeholder="Position"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input 
              type="text"
              name="profile.company.department"
              value={formData.profile.company.department}
              onChange={handleChange}
              placeholder="Department"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="space-y-4">
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-blue-600 text-white py-3 rounded-lg transition-colors ${
                loading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'
              }`}
            >
              {loading ? 'Creating User...' : 'Create User'}
            </button>

            <Link 
              to="/users" 
              className="block w-full text-center text-blue-600 hover:underline"
            >
              Back to Users List
            </Link>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default AddUser; 